import React from "react";
import { WifiOff, Inbox, AlertTriangle, RefreshCw, Lock } from "lucide-react";
import { ApiError } from "@/types/api";
import { useI18n } from "@/i18n";

export function LoadingCards({ count = 4, height = "h-40" }: { count?: number; height?: string }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="loading-state">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className={`skeleton ${height} rounded-2xl`} />
      ))}
    </div>
  );
}

export function LoadingRows({ count = 6 }: { count?: number }) {
  return (
    <div className="space-y-3" data-testid="loading-state">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="skeleton h-16 rounded-xl" />
      ))}
    </div>
  );
}

export function EmptyState({ title, hint, icon }: { title: string; hint?: string; icon?: React.ReactNode }) {
  return (
    <div className="panel flex flex-col items-center justify-center gap-3 px-6 py-14 text-center" data-testid="empty-state">
      <div className="grid h-12 w-12 place-items-center rounded-full bg-white/5 text-muted-foreground">
        {icon || <Inbox className="h-6 w-6" />}
      </div>
      <p className="text-base font-semibold text-foreground/90">{title}</p>
      {hint && <p className="max-w-sm text-sm text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error?: ApiError | null; onRetry?: () => void }) {
  const { d } = useI18n();
  const isNet = error?.kind === "network" || error?.kind === "timeout";
  const isAuth = error?.kind === "unauthorized" || error?.kind === "forbidden";
  const msg = isAuth ? d.states.unauthorized : isNet ? d.states.noConnection : d.states.unavailable;
  return (
    <div className="panel flex flex-col items-center justify-center gap-3 px-6 py-14 text-center" data-testid="error-state">
      <div className={`grid h-12 w-12 place-items-center rounded-full ${isAuth ? "bg-gold/10 text-gold-400" : "bg-live/10 text-live-400"}`}>
        {isAuth ? <Lock className="h-6 w-6" /> : isNet ? <WifiOff className="h-6 w-6" /> : <AlertTriangle className="h-6 w-6" />}
      </div>
      <p className="text-base font-semibold text-foreground/90">{msg}</p>
      {onRetry && !isAuth && (
        <button
          onClick={onRetry}
          data-testid="error-retry-button"
          className="mt-1 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-foreground transition hover:border-white/25 hover:bg-white/10"
        >
          <RefreshCw className="h-4 w-4" /> {d.common.retry}
        </button>
      )}
    </div>
  );
}

// Convenience wrapper for query-driven sections.
export function AsyncSection<T>({
  isLoading, isError, error, data, onRetry, loading, empty, isEmpty, children,
}: {
  isLoading: boolean;
  isError: boolean;
  error?: ApiError | null;
  data?: T;
  onRetry?: () => void;
  loading?: React.ReactNode;
  empty?: React.ReactNode;
  isEmpty?: (d: T) => boolean;
  children: (d: T) => React.ReactNode;
}) {
  if (isLoading) return <>{loading || <LoadingCards />}</>;
  if (isError) return <ErrorState error={error} onRetry={onRetry} />;
  if (data == null) return <ErrorState error={error} onRetry={onRetry} />;
  if (isEmpty && isEmpty(data)) return <>{empty || <EmptyState title="—" />}</>;
  return <>{children(data)}</>;
}
